// Tiered Rewards Bar Component
// Displays progressive rewards based on cart total

// Format money utility function
function formatMoney(cents) {
  if (typeof cents !== "number") cents = Number(cents) || 0;
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

var TieredRewardsBar = class extends HTMLElement {
  static get observedAttributes() {
    return ["total-price"];
  }

  constructor() {
    super();

    this._onCartChangedListener = this._onCartChanged.bind(this);
    this._isManagingGifts = false;

    // Reference to the un-patched fetch, used for our own cart requests so they
    // never re-trigger our fetch interceptor (prevents the add/update loop).
    this._originalFetch = window.fetch.bind(window);

    // Cooldown to avoid hammering /cart/add.js when a request fails or is throttled (429).
    this._lastGiftActionAt = 0;
    this._giftActionCooldown = 3000;

    // Brindes que saíram da escada: se um carrinho antigo ainda tiver um deles, ele sai.
    // (Refil Shampoo e Refil Condicionador Personalizado saíram quando a escada virou frete fixo + 5% + 10%.)
    this.retiredGiftVariants = [49287240352052, 49286749618484, 48936994111796];

    // Degraus com brinde (em centavos). A escada atual não tem brinde: só frete e desconto,
    // que não passam por aqui. Brinde novo = { amount, label, reward, variantId }.
    this.tiers = [];
  }

  async connectedCallback() {
    // Patamares vindos do Liquid (ex.: tela "Seus produtos"): atributo tiers='[{"amount":24900,"reward":"...","variantId":123}]'
    // Sem o atributo, usa a lista fixa definida no constructor (drawer do carrinho).
    this._loadTiersFromAttribute();

    // Inicializa com valor real do carrinho
    this._updateFromCart();

    // Eventos
    document.addEventListener("cart:change", this._onCartChangedListener);
    document.addEventListener("cart:update", this._onCartChangedListener);
    document.addEventListener("cart:add", this._onCartChangedListener);

    // Clique em botão de add to cart
    document.addEventListener("click", (e) => {
      const btn = e.target.closest('form[action*="/cart/add"] button, [name="add"]');
      if (btn) {
        setTimeout(() => this._updateFromCart(), 500);
      }
    });

    // Intercepta requisições AJAX do carrinho
    this._interceptCartRequests();

    // Confere os brindes antes de ir para o checkout
    this._guardCheckout();
  }

  _loadTiersFromAttribute() {
    const raw = this.getAttribute('tiers');
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) {
        this.tiers = parsed
          .map((t) => ({
            amount: Number(t.amount) || 0,
            label: String(t.label || Math.round((Number(t.amount) || 0) / 100)),
            reward: String(t.reward || ''),
            variantId: t.variantId ? Number(t.variantId) : undefined,
          }))
          .filter((t) => t.amount > 0)
          .sort((a, b) => a.amount - b.amount);
      }
    } catch (e) {
      console.warn('[tiered-rewards-bar] atributo tiers inválido:', e);
    }
  }

  disconnectedCallback() {
    document.removeEventListener("cart:change", this._onCartChangedListener);
    clearTimeout(this._retryTimeout);
    document.removeEventListener("cart:update", this._onCartChangedListener);
    document.removeEventListener("cart:add", this._onCartChangedListener);
  }

  get totalPrice() {
    return parseFloat(this.getAttribute("total-price") || "0") || 0;
  }

  set totalPrice(value) {
    this.setAttribute("total-price", String(value));
  }

  attributeChangedCallback(attribute, oldValue, newValue) {
    if (attribute === "total-price" && oldValue !== newValue) {
      this._updateRewards();
    }
  }

  async _updateFromCart() {
    // Debounce/Throttle multiple rapid calls
    if (this._updateTimeout) clearTimeout(this._updateTimeout);
    
    this._updateTimeout = setTimeout(async () => {
      try {
        const timestamp = Date.now();
        const res = await this._originalFetch(`/cart.js?v=${timestamp}`);
        if (!res.ok) throw new Error(`cart.js responded ${res.status}`);
        const cart = await res.json();
        const price = this._calculateCleanPrice(cart);
         
        if (this.totalPrice === price) {
          // Even if price is same, force a rewards update to ensure UI is in sync
          this._updateRewards();
        } else {
          this.totalPrice = price;
        }
      } catch (e) {
        console.error('Erro ao buscar carrinho:', e);
      }
    }, 100);
  }

  _calculateCleanPrice(cart) {
    if (!cart || !cart.items) return 0;
    
    // Calculate total price excluding items marked as gifts
    const giftPrice = cart.items.reduce((acc, item) => {
      if (this._isGiftItem(item)) {
        return acc + (item.final_line_price || 0);
      }
      return acc;
    }, 0);
    
    const totalPrice = cart.total_price || 0;
    return Math.max(0, totalPrice - giftPrice);
  }

  _isGiftItem(item) {
    if (!item || !item.properties) return false;

    // "_gift" é a marcação atual (a Shopify esconde propriedades com "_" no checkout).
    // "gift" fica por compatibilidade com carrinhos montados antes da troca.
    const hiddenGiftProp = item.properties._gift;
    const giftProp = item.properties.gift;
    const freeProductProp = item.properties._free_product;
    const ruleIdProp = item.properties._rule_id;
    const kitePromo = item.properties._kite_promo_name;

    return (
      hiddenGiftProp === 'true' ||
      hiddenGiftProp === true ||
      giftProp === 'true' ||
      giftProp === true ||
      freeProductProp === 'true' ||
      freeProductProp === true ||
      (typeof ruleIdProp === 'string' && ruleIdProp.includes('free_gift')) ||
      typeof kitePromo === 'string'
    );
  }

  _updateRewards() {
    const currentPrice = this.totalPrice;
    const maxTier = this.tiers.length ? this.tiers[this.tiers.length - 1].amount : 0;
    const progress = maxTier ? Math.min(1, currentPrice / maxTier) : 0;

    // Progress bar
    const progressBar = this.querySelector(".tiered-rewards-bar__progress");
    if (progressBar) {
      progressBar.style.setProperty("--progress", `${progress}`);
    }

    // Tiers
    this.tiers.forEach((tier, index) => {
      const tierElement = this.querySelector(`[data-tier="${index}"]`);
      if (tierElement) {
        const isActive = currentPrice >= tier.amount;
        tierElement.classList.toggle("tiered-rewards-bar__tier--active", isActive);
        tierElement.classList.toggle("tiered-rewards-bar__tier--inactive", !isActive);
      }
    });

    // Mensagem
    const topMessageElement = this.querySelector(".tiered-rewards-bar__top-message");
    if (topMessageElement) {
      const nextTier = this.tiers.find(t => t.amount > currentPrice);

      if (!this.tiers.length) {
        topMessageElement.innerHTML = '';
      } else if (nextTier) {
        const remaining = nextTier.amount - currentPrice;
        const formattedRemaining = formatMoney(remaining);

        topMessageElement.innerHTML =
          `Adicione mais <strong>${formattedRemaining}</strong> e ganhe <strong>${nextTier.reward}</strong>`;
      } else {
        topMessageElement.innerHTML =
          "🎉 Parabéns! Você desbloqueou todas as recompensas!";
      }
    }

    // Manage gifts (throttle)
    if (!this._isManagingGifts) {
      this._manageGifts();
    }
  }

  async _manageGifts(force = false, depth = 0) {
    // Ocupado (esta ou outra barra da página): marca para conferir de novo ao terminar.
    // Antes a mudança era ignorada e o brinde podia ficar no carrinho sem atingir o valor.
    if (this._isManagingGifts || window.__tieredRewardsManaging) {
      window.__tieredRewardsPending = true;
      return;
    }

    // Avoid retrying a gift add/remove too quickly (e.g. after a 429 throttle).
    // Instead of giving up, schedule one re-check for when the cooldown ends,
    // so a second tier reached in the same action still gets its gift.
    const sinceLast = Date.now() - this._lastGiftActionAt;
    if (!force && sinceLast < this._giftActionCooldown) {
      clearTimeout(this._retryTimeout);
      this._retryTimeout = setTimeout(() => this._updateFromCart(), this._giftActionCooldown - sinceLast + 50);
      return;
    }

    this._isManagingGifts = true;
    window.__tieredRewardsManaging = true;
    window.__tieredRewardsPending = false;
    let changed = false;

    try {
      let cart = await this._getCart();
      // Valor do carrinho que acabou de ser lido, não o guardado na barra (podia estar desatualizado).
      const currentPrice = this._calculateCleanPrice(cart);
      this.totalPrice = currentPrice;

      // Nunca mais de 1 unidade de cada brinde: se houver 2+ (linhas duplicadas ou quantidade
      // aumentada pelo cliente), reduz para 1 e zera as linhas extras.
      const fixes = {};
      for (const tier of this.tiers) {
        if (!tier.variantId) continue;
        const giftLines = cart.items.filter(item => item.variant_id == tier.variantId && this._isGiftItem(item));
        const total = giftLines.reduce((acc, item) => acc + (item.quantity || 0), 0);
        if (total > 1) {
          giftLines.forEach((line, index) => {
            fixes[line.key] = index === 0 ? 1 : 0;
          });
        }
      }
      cart.items.forEach(item => {
        if (this.retiredGiftVariants.includes(Number(item.variant_id)) && this._isGiftItem(item)) {
          fixes[item.key] = 0;
        }
      });
      if (Object.keys(fixes).length) {
        const fixed = await this._updateLines(fixes);
        if (fixed) cart = fixed;
      }

      for (const tier of this.tiers) {
        if (tier.variantId) {
          const hasGift = cart.items.some(item => item.variant_id == tier.variantId && this._isGiftItem(item));
          
          if (currentPrice >= tier.amount && !hasGift) {
            await this._addGift(tier.variantId);
            changed = true;
            break;
          } else if (currentPrice < tier.amount && hasGift) {
            await this._removeGift(tier.variantId);
            changed = true;
            break;
          }
        }
      }
    } catch (e) {
      console.error('Erro ao gerenciar brindes:', e);
    } finally {
      this._isManagingGifts = false;
      window.__tieredRewardsManaging = false;
    }
    // Mexeu num brinde ou chegou mudança no meio: confere de novo (o próximo patamar ou o carrinho novo).
    // depth limita as repetições (ex.: brinde esgotado que nunca entra).
    if ((changed || window.__tieredRewardsPending) && depth < 4) {
      window.__tieredRewardsPending = false;
      return this._manageGifts(true, depth + 1);
    }
  }

  /** Última conferência antes do checkout: acerta os brindes com o carrinho atual e só então segue. */
  _guardCheckout() {
    if (window.__tieredRewardsCheckoutGuard) return;
    window.__tieredRewardsCheckoutGuard = true;
    document.addEventListener('click', async (e) => {
      const trigger = e.target.closest('[name="checkout"], [data-routine-checkout], a[href$="/checkout"], a[href*="/checkout?"]');
      if (!trigger || trigger.dataset.giftsChecked === 'true') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      trigger.setAttribute('aria-busy', 'true');
      try {
        for (let i = 0; i < 20 && (this._isManagingGifts || window.__tieredRewardsManaging); i += 1) {
          await new Promise((r) => setTimeout(r, 150));
        }
        await this._manageGifts(true);
      } finally {
        trigger.removeAttribute('aria-busy');
      }
      trigger.dataset.giftsChecked = 'true';
      trigger.click();
      setTimeout(() => { delete trigger.dataset.giftsChecked; }, 1000);
    }, true);
  }

  /** Atualiza linhas por chave ({ key: quantidade }) e devolve o carrinho resultante. */
  async _updateLines(updates) {
    const sections = this._getSectionsToUpdate();
    this._lastGiftActionAt = Date.now();
    try {
      const response = await this._originalFetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates, sections: sections.join(','), sections_url: window.location.pathname })
      });
      if (!response.ok) {
        console.warn(`Não foi possível ajustar quantidade de brinde (HTTP ${response.status}).`);
        return null;
      }
      const data = await response.json();
      this._refreshUI(data, sections);
      return data;
    } catch (e) {
      console.error('Erro ao ajustar brinde:', e);
      return null;
    }
  }

  async _getCart() {
    const res = await this._originalFetch(`/cart.js?v=${Date.now()}`);
    if (!res.ok) throw new Error(`cart.js responded ${res.status}`);
    return await res.json();
  }

  _getSectionsToUpdate() {
    const sections = new Set();
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sections.add(item.dataset.sectionId);
      }
    });
    
    // Add default header section if found
    const headerSection = document.querySelector('[id*="header_section"]');
    if (headerSection && headerSection.id) {
        sections.add(headerSection.id.replace('shopify-section-', ''));
    }

    return Array.from(sections);
  }

  async _addGift(variantId) {
    // Última checagem com o carrinho fresco: se o brinde já entrou por outra instância/aba, não duplica.
    try {
      const fresh = await this._getCart();
      if (fresh.items.some(item => item.variant_id == variantId && this._isGiftItem(item))) return;
    } catch (e) {}

    const sections = this._getSectionsToUpdate();
    const body = {
      id: variantId,
      quantity: 1,
      properties: { _gift: 'true' },
      sections: sections.join(','),
      sections_url: window.location.pathname
    };

    this._lastGiftActionAt = Date.now();

    try {
      const response = await this._originalFetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      // A throttled (429) or errored request returns an HTML page, not JSON.
      // Bail out instead of trying to parse it (would throw "Unexpected token '<'").
      if (!response.ok) {
        console.warn(`Não foi possível adicionar brinde (HTTP ${response.status}). Tentando novamente mais tarde.`);
        return;
      }

      const responseData = await response.json();

      // Since add.js doesn't return the full cart object (only the added item),
      // we fetch the full cart to have the correct total_price for the progress bar.
      const cart = await this._getCart();
      if (responseData.sections) {
        cart.sections = responseData.sections;
      }

      this._refreshUI(cart, sections);
    } catch (e) {
      console.error('Erro ao adicionar brinde:', e);
    }
  }

  async _removeGift(variantId) {
    try {
      const cart = await this._getCart();
      const item = cart.items.find(item => item.variant_id == variantId && this._isGiftItem(item));
      if (item) {
        const sections = this._getSectionsToUpdate();
        const updates = {};
        updates[item.key] = 0;

        const body = {
          updates,
          sections: sections.join(','),
          sections_url: window.location.pathname
        };

        this._lastGiftActionAt = Date.now();

        const response = await this._originalFetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });

        if (!response.ok) {
          console.warn(`Não foi possível remover brinde (HTTP ${response.status}).`);
          return;
        }

        const responseData = await response.json();
        this._refreshUI(responseData, sections);
      }
    } catch (e) {
      console.error('Erro ao remover brinde:', e);
    }
  }

  _refreshUI(data, sections) {
    if (!data) return;

    // 1. Dispatch event for other components
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: data,
        data: {
          sections: data.sections || {},
          source: 'tiered-rewards-bar'
        }
      }
    }));

    // 2. Manual morph fallback
    if (data.sections && window.morphSection) {
      for (const sectionId in data.sections) {
        try {
          window.morphSection(sectionId, data.sections[sectionId]);
        } catch (e) {
          console.warn(`Could not morph section ${sectionId}:`, e);
        }
      }
    }

    // 3. Update total price attribute to reflect new state
    const cleanPrice = this._calculateCleanPrice(data);
    if (cleanPrice !== undefined && cleanPrice !== null) {
      this.totalPrice = cleanPrice;
    } else {
      this._updateFromCart();
    }

    // 4. Force update of rewards/progress bar (important if price hasn't changed but DOM was morphed)
    this._updateRewards();
  }

  _onCartChanged(event) {
    // Avoid recursion if we are the one who triggered the update
    if (event.detail?.data?.source === 'tiered-rewards-bar') return;

    const cart = this._getCartFromEvent(event);
    if (!cart) return;

    // Calculate price excluding gifts
    const price = this._calculateCleanPrice(cart);

    this.totalPrice = price;
  }

  _getCartFromEvent(event) {
    if (!event?.detail) return null;

    const cart = (
      event.detail.resource ||
      event.detail.cart ||
      event.detail.data?.cart ||
      event.detail.data?.resource ||
      event.detail ||
      null
    );

    // If it's just a line item (e.g. from /cart/add.js), it won't have total_price.
    // We should only return it if it looks like a full cart.
    if (cart && cart.total_price === undefined && cart.items === undefined) {
      return null;
    }

    return cart;
  }

  _interceptCartRequests() {
    // Only patch window.fetch once, even if several bars are on the page,
    // to avoid stacking interceptors that each fire a refresh.
    if (window.__tieredRewardsFetchPatched) return;
    window.__tieredRewardsFetchPatched = true;

    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const url = typeof args[0] === 'string' ? args[0] : (args[0] instanceof URL ? args[0].href : (args[0] && args[0].url) || '');
      const response = await originalFetch.apply(window, args);

      if (url.includes('/cart/') && !url.includes('/cart.js')) {
        const method = ((args[1] && args[1].method) || '').toUpperCase();
        const isMutation = method === 'POST' || method === 'PATCH' || method === 'DELETE';

        // Skip refreshes for requests we triggered ourselves (gift add/remove use
        // the un-patched fetch, but this flag is a second safeguard against loops).
        if (isMutation && !this._isManagingGifts) {
          this._updateFromCart();
        }
      }
      return response;
    };
  }
};

// Register component
if (!window.customElements.get("tiered-rewards-bar")) {
  window.customElements.define("tiered-rewards-bar", TieredRewardsBar);
}